import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Form,
    InputGroup,
    Button,
    Spinner,
    Modal,
    Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import EditCompanyInformation from "./CompanyInformation";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { LOADING, SET_CURRENT_WEBSITE, SET_MESSAGE } from "../../Actions/Types";
import { updateUserService } from "../../Services/UserAction/UpdateUserDetails";
import { deleteAccountDetails } from "../../Services/UserAction/DeleteAccountService";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import getBaseURL from "../../Helpers/getBaseURL";
import { verifyLoginCode } from "../../Api/TwoFactorAuthAPI";
import { getUserDetailsApi } from "../../Api/UserAction/LoginActionApi";
import { useTheme } from "../../Context/ThemeContext";
import select2LangPropTypes from "../../PropTypes/select2LangPropTypes";
import Select from "react-select";
import { getCountryListService } from "../../Services/BillingAddressService";
import getEUCountryListwithID from "../../Helpers/EUCountryListWithId";
import EUServerMigrationPopup from "../EUServerMigrationPopup";
import { postUserActivityListService } from "../../Services/UserActivityService";
import { UpdateEUFlagDomainService } from "../../Services/DomainService/UpdateEUFlagDomain";
import compareDates from "../../Helpers/DateCompare";
import isGreater from "../../Helpers/DateCompare";

const darkThemeStyles = {
    container: {
        backgroundColor: '#0F0F1A',
    },
    input: {
        backgroundColor: 'black',
        borderColor: '#555',
        color: '#fff',
    },
    button: {
        backgroundColor: '#333',
        borderColor: '#555',
        ':hover': {
            backgroundColor: '#444',
        },
    },
    dropdown: {
        backgroundColor: '#0F0F1A',
        color: '#fff',
        ':hover': {
            backgroundColor: 'black',
        },
    },
    search: {
        backgroundColor: 'black',
        color: 'black',
        ':hover': {
            backgroundColor: 'black',
        },
    },
};


const euServerStartDate = `${process.env.REACT_APP_EU_START_DATE}`;

// component to display edit personal information form
const EditPersonalInformation = () => {
    const { t } = useTranslation();

    const theme = useTheme()

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        user,
        accessToken,
        activeLanguage,
        languageList,
        activeEditLanguage,
        isUserDeleted,
        currentWebsite,
    } = useSelector((store: StoreProptypes) => store);
    console.log("user: ", user?.is_eu_script_show);

    //const [title, setTitle] = useState('');
    const [fullName, setFullName] = useState("");
    //const [website, setWebsite] = useState('');
    const [userEmail, setUserEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [language, setLanguage] = useState("");

    const [companyWebsiteURL, setCompanyWebsiteURL] = useState("");
    const [companyName, setCompanyName] = useState("");
    const { logout, isAuthenticated } = useAuth0();
    const [isDeleteAccountConfirmed, setIsDeleteAccountConfirmed] =
        useState<boolean>(false);
    const [isAccountDeleted, setIsAccountDeleteted] = useState<boolean>(false);
    const [isGoogle2FAEnabled, setIsGoogle2FAEnabled] = useState<Boolean>(false);
    const [isShowGoogle2FAEnabledScreen, setIsShowGoogle2FAEnabledScreen] =
        useState<Boolean>(false);
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [showEmailChangeConfirm, setShowEmailChangeConfirm] = useState(false);
    const [newEmailTemp, setNewEmailTemp] = useState("");
    const [country, setCountry] = useState<select2LangPropTypes>();
    const [countryList, setCountryList] = useState<select2LangPropTypes[]>([]);
    const [isCountryChanged, setIsCountryChanged] = useState<boolean>(false);
    const [isMigratedFromNonEU, setIsMigratedFromNonEU] =
        useState<boolean>(false);
    const getEUCoutryListsId = getEUCountryListwithID();

    useEffect(() => {
        if (accessToken) {
            // service to get card list
            getCountryListService(accessToken, setCountryList);
        }
    }, [accessToken]);

    // const [message, setMessage] = useState('');
    // const [showAlert, setAlert] = useState(false);

    useEffect(() => {
        if (activeEditLanguage) {
            setLanguage(activeEditLanguage.code);
        } else {
            setLanguage("en");
        }
    }, [activeEditLanguage]);

    const getUser = (): void => {
        if (accessToken) {
            getUserDetailsApi(accessToken)
                .then((res) => {
                    if (res?.Data) {
                        setIsGoogle2FAEnabled(res?.Data?.is_google2fa_enabled === 1);
                    }
                })
                .catch((err) => {
                    dispatch({
                        type: SET_MESSAGE,
                        payload: {
                            toast_message: `${err}`,
                            toast_type: "danger",
                        },
                    });
                });
        }
    };

    useEffect(() => {
        getUser();
        if (!isAccountDeleted && isUserDeleted) {
            navigate("/front/login?logout");
        }
    }, [user, navigate]);

    useEffect(() => {
        // check if user data is available
        if (user) {
            user.name && setFullName(user.name);
            user.email && setUserEmail(user.email);
            user.email && setNewEmailTemp(user.email);
            user.company_name && setCompanyName(user.company_name);
            user.phone && setPhone(user.phone);
            user.website && setCompanyWebsiteURL(user.website);
        }
    }, [user]);

    // apply temp email (if changed)
    const proceedUpdate = (finalEmail: string) => {
        // Validation (same as your existing validation)
        if (finalEmail === "") {
            dispatch({
                type: SET_MESSAGE,
                payload: {
                    toast_message: t("Please enter your email address"),
                    toast_type: "danger",
                },
            });
            return;
        }

        if (!/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/g.test(finalEmail)) {
            dispatch({
                type: SET_MESSAGE,
                payload: {
                    toast_message: t("Please enter valid email address."),
                    toast_type: "danger",
                },
            });
            return;
        }

        if (phone === "") {
            dispatch({
                type: SET_MESSAGE,
                payload: {
                    toast_message: t("Please enter your phone number."),
                    toast_type: "danger",
                },
            });
            return;
        }

        if (fullName === "") {
            dispatch({
                type: SET_MESSAGE,
                payload: {
                    toast_message: t("Please enter your full name."),
                    toast_type: "danger",
                },
            });
            return;
        }

        // check if user and access token is available, then call update user service
        var findCountry = [];
        if (country) {
            findCountry = getEUCoutryListsId.filter(
                (d) => d.id.toString() == country.value
            );
        }

        user &&
        accessToken &&
        updateUserService(
            {
                ...user,
                name: fullName,
                phone: phone,
                email: finalEmail,
                company_name: companyName,
                default_lang_code: language,
                website: companyWebsiteURL,
                country: country ? Number(country.value) : 0,
            },
            accessToken,
            dispatch,
            t,
            undefined,
            undefined,
            languageList || []
        );

        let is_eu_script_show = 1;
        let no_required_eu = 1;

        if (isCountryChanged) {
            is_eu_script_show = 0; // when country changed, we reset script show

            if (findCountry.length > 0) {
                no_required_eu = 0; // if country list found
            } else {
                no_required_eu = 1; // fallback
            }

            accessToken &&
            currentWebsite &&
            UpdateEUFlagDomainService(
                accessToken,
                dispatch,
                currentWebsite.id,
                is_eu_script_show,
                currentWebsite.is_eu_script_show_first,
                no_required_eu
            ).then((d) => {
                if (d.status === 200) {
                    if (isCountryChanged && findCountry.length > 0 && accessToken) {
                        postUserActivityListService(
                            accessToken,
                            "Migrate to EU",
                            "User requested to migrate EU"
                        );
                    }
                    dispatch({
                        type: SET_CURRENT_WEBSITE,
                        payload: {
                            ...currentWebsite,
                            ...(isCountryChanged ? { is_eu_script_show: 0 } : {}),
                            ...(isCountryChanged && findCountry.length > 0
                                ? { no_required_eu: 0 }
                                : {}),
                        },
                    });
                }
            });
        }

        setIsCountryChanged(false);
    };

    useEffect(() => {
        // check if user data is available
        if (user) {
            user.country &&
            setCountry(countryList.find((d) => d.value == user.country.toString()));
            console.log("countryList: ", countryList);
        }
    }, [user, countryList]);

    // function to handle form submit
    const HandleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (newEmailTemp && newEmailTemp !== userEmail) {
            // email changed → show confirmation modal
            setShowEmailChangeConfirm(true);
            return;
        }

        proceedUpdate(userEmail);
    };

    // function to hide widget script modal
    const handleDeleteAccountModel = () => {
        setIsShowGoogle2FAEnabledScreen(false);
        setIsDeleteAccountConfirmed(false);
        setOtp("");
        setError("");
    };

    // function to hide widget script modal
    const handleAccountDeletedModelModel = () => {
        setIsShowGoogle2FAEnabledScreen(false);
        setIsDeleteAccountConfirmed(false);
    };

    const deleteAccount = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDeleteAccountConfirmed(true);
    };

    const confirmDeleteAccount = async () => {
        if (isGoogle2FAEnabled && !isShowGoogle2FAEnabledScreen) {
            setIsShowGoogle2FAEnabledScreen(true);
        } else if (isShowGoogle2FAEnabledScreen && accessToken) {
            const trimmedCode = otp.trim();

            // Validate 6-digit OTP or 8–24 character alphanumeric recovery code
            const isValidOTP = /^\d{6}$/.test(trimmedCode);
            const isValidRecovery = /^[a-zA-Z0-9]{8,24}$/.test(trimmedCode);

            if (!isValidOTP && !isValidRecovery) {
                setError(t("Please enter a valid 6-digit code or recovery code."));
                return;
            }

            // Clear error and show loader
            dispatch({
                type: LOADING,
                payload: false,
            });
            setError("");

            try {
                // Call verification API
                const response = await verifyLoginCode({
                    token: accessToken,
                    code: trimmedCode,
                });

                // If verification fails
                if (!response?.status) {
                    setError(t("Verification failed. Please try again."));
                } else {
                    // Dispatch success state to Redux
                    deleteAccountDetails(accessToken, dispatch, navigate, t);
                    setIsDeleteAccountConfirmed(false);
                    setIsAccountDeleteted(true);
                }
            } catch {
                // Catch any request error
                setError(t("Error verifying 2FA. Please try again."));
            }
        } else {
            if (accessToken) {
                deleteAccountDetails(accessToken, dispatch, navigate, t);
                setIsDeleteAccountConfirmed(false);
                setIsAccountDeleteted(true);
            }
        }
    };

    const navigateDeletedAccount = () => {
        if (isAuthenticated) {
            dispatch({
                type: LOADING,
                payload: false,
            });
            const baseURL = getBaseURL();
            setTimeout(() => {
                logout({ logoutParams: { returnTo: `${baseURL}/front/login?logout` } });
            }, 300);
        } else {
            navigate("/front/login?logout");
            // Loading false
            dispatch({
                type: LOADING,
                payload: false,
            });
        }
    };

    const revertBackToOriginalLanguage = () => {
        if (user) {
            user.country &&
            setCountry(countryList.find((d) => d.value == user.country.toString()));
        }
        setIsCountryChanged(false);
        setIsMigratedFromNonEU(false);
    };

    return (
        <>
            <Form>
                <div className="card">
                    <EditCompanyInformation
                        companyWebsiteURL={companyWebsiteURL}
                        setCompanyWebsiteURL={setCompanyWebsiteURL}
                        companyName={companyName}
                        setCompanyName={setCompanyName}
                    />
                    <div className="card-header">
                        <h2 className="mb-0 h6">{t("Personal Information")}</h2>
                    </div>
                    <div className="card-body">
                        <Form.Label htmlFor="fullName">
                            {" "}
                            {t("Full Name")}{" "}
                            <span className="text-danger" aria-hidden="true">
                *
              </span>{" "}
                        </Form.Label>
                        <InputGroup className="mb-3 input-group-outline">
                            <Form.Control
                                id="fullName"
                                type="text"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setFullName(e.target.value)
                                }
                                value={fullName}
                                size="lg"
                                required
                            />
                        </InputGroup>
                        <Form.Label htmlFor="email">
                            {" "}
                            {t("E-Mail")}{" "}
                            <span className="text-danger" aria-hidden="true">
                *
              </span>{" "}
                        </Form.Label>
                        <InputGroup className="mb-3 input-group-outline">
                            <Form.Control
                                id="email"
                                type="email"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setNewEmailTemp(e.target.value)
                                }
                                value={newEmailTemp}
                                size="lg"
                                required
                            />
                        </InputGroup>
                        <Form.Label htmlFor="email">
                            {" "}
                            {t("Language")}{" "}
                            <span className="text-danger" aria-hidden="true"></span>{" "}
                        </Form.Label>
                        <InputGroup className="mb-3 input-group-outline">
                            <Form.Select
                                required
                                value={language}
                                onChange={(e) => setLanguage(e.currentTarget.value)}
                            >
                                {languageList &&
                                languageList.map((item) => (
                                    <option value={item.code}>{t(item.original_name)}</option>
                                ))}
                            </Form.Select>
                        </InputGroup>
                        <Form.Label htmlFor="phone"> {t("Phone")} <span className="text-danger" aria-hidden="true">*</span> </Form.Label>
                        <InputGroup className={`mb-3 input-group-outline phone-number ${theme.theme === 'dark' ? 'darkmode' : ''}`}>
                            <PhoneInput
                                country={"us"}
                                onChange={(phone) => setPhone(phone)}
                                value={phone}
                                inputProps={{id:"phone", required:"required"}}
                                containerStyle={theme.theme === 'dark' ? darkThemeStyles.container : {}}
                                inputStyle={theme.theme === 'dark' ? darkThemeStyles.input : {}}
                                buttonStyle={theme.theme === 'dark' ? darkThemeStyles.button : {}}
                                dropdownStyle={theme.theme === 'dark' ? darkThemeStyles.dropdown : {}}
                                searchStyle={theme.theme === 'dark' ? darkThemeStyles.search : {}}
                            />
                        </InputGroup>
                        {/* <Form.Label htmlFor="country"> {t("Country")} </Form.Label>
                        <InputGroup className="mb-3 input-group-outline">
                            <Select
                                id="language_select"
                                onChange={(e) => {
                                    setCountry({
                                        label: e ? e.label : "",
                                        value: e ? String(e.value) : "",
                                    });
                                    if (currentWebsite?.is_eu_script_show !== 0) {
                                        setIsCountryChanged(true);
                                    } else {
                                        setIsCountryChanged(false);
                                    }
                                    if (e) {
                                        const findCountry = getEUCoutryListsId.filter(
                                            (d) => d.id.toString() == e.value
                                        );
                                        setIsMigratedFromNonEU(
                                            currentWebsite?.is_eu_script_show !== 0 &&
                                            findCountry.length > 0
                                                ? true
                                                : false
                                        );
                                    }
                                }}
                                options={countryList}
                                defaultValue={country}
                                value={country}
                                closeMenuOnSelect={true}
                                backspaceRemovesValue={true}
                                isClearable={false}
                                isSearchable={true}
                                placeholder={`${t("Select Country")}...`}
                                className={`aioa_dashboard-custom-country-select w-100 rounded-2`}
                                aria-label={t("Select Country")}
                                classNamePrefix="lang-select"
                                theme={(theme) => ({
                                    ...theme,
                                    borderRadius: 0,
                                    colors: {
                                        ...theme.colors,
                                        primary25: "#d9cce6",
                                        primary: "#420083",
                                    },
                                })}
                            />
                        </InputGroup>*/}
                        <div className="text-center">
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                className={activeLanguage?.is_rtl ? "me-auto" : "ms-auto"}
                                style={{ marginRight: 20 }}
                                onClick={(e) => HandleSubmit(e)}
                            >
                                <>{t("Update Profile")}</>
                            </Button>
                            <Button
                                type="submit"
                                variant="danger"
                                size="lg"
                                className={activeLanguage?.is_rtl ? "me-auto" : "ms-auto"}
                                onClick={(e) => deleteAccount(e)}
                            >
                                <>{t("Delete Account")}</>
                            </Button>
                        </div>
                    </div>
                </div>
            </Form>

            {/* Confirm Popup for Delete Account Website */}
            <Modal
                show={isDeleteAccountConfirmed}
                onHide={() => handleDeleteAccountModel()}
                size="lg"
                fullscreen="lg-down"
                backdrop="static"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("delete account")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isShowGoogle2FAEnabledScreen ? (
                        <div className="mb-3">
                            <div className="mb-3">
                                {t(
                                    "This action is permanent. Your account and all related data will be deleted within 48 hours, and you will no longer be able to access them."
                                )}
                            </div>
                            <b>{t("are you sure you want to delete your account?")}</b>
                        </div>
                    ) : (
                        <>
                            {" "}
                            <Form.Label htmlFor="otp">
                                {t("Authentication Code or Recovery Code")}{" "}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    type="text"
                                    aria-label="OTP"
                                    placeholder={t("Enter 6-digit OTP or recovery code")}
                                    onChange={(e) => setOtp(e.target.value)}
                                    value={otp}
                                    maxLength={24}
                                    size="lg"
                                />
                            </InputGroup>
                            {error && (
                                <Alert variant="danger" className="mt-3">
                                    {error}
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        size="lg"
                        onClick={() => confirmDeleteAccount()}
                    >
                        {t("Confirm")}
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => handleDeleteAccountModel()}
                    >
                        {t("Cancel")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete information popu[] */}
            <Modal
                show={isAccountDeleted}
                onHide={() => navigateDeletedAccount()}
                size="lg"
                fullscreen="lg-down"
                backdrop="static"
                centered
            >
                <Modal.Header>
                    <Modal.Title>{t("Account Deleted")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t(
                        "Your account deletion request has been submitted successfully. You will no longer be able to access your account and all your data will be permanently removed from our system within 48 hours. If you have any questions, please contact us at hello@skynettechnologies.com"
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => navigateDeletedAccount()}
                    >
                        {t("Ok")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showEmailChangeConfirm}
                onHide={() => setShowEmailChangeConfirm(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("Change Email?")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t("Are you sure you want to change your email address?")}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        size="lg"
                        onClick={() => {
                            setUserEmail(newEmailTemp);
                            setShowEmailChangeConfirm(false);
                            proceedUpdate(newEmailTemp);
                        }}
                    >
                        {t("Yes, Change")}
                    </Button>

                    <Button
                        variant="secondary"
                        size="lg"
                        onClick={() => {
                            setShowEmailChangeConfirm(false);
                            setNewEmailTemp(userEmail); // reset change
                        }}
                    >
                        {t("Cancel")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* comformation popup for country change */}
            {isMigratedFromNonEU && currentWebsite ? (
                <EUServerMigrationPopup
                    isShow={isMigratedFromNonEU ? 1 : 0}
                    isMigratedFlag={true}
                    isFirstTimeLoad={false}
                    widgetAPIKey={
                        currentWebsite.current_package_only &&
                        currentWebsite.current_package_only.length > 0
                            ? currentWebsite.current_package_only[0].api_key
                            : null
                    }
                    widgetPosition={
                        currentWebsite.widget_position !== null
                            ? currentWebsite.widget_position
                            : `bottom_right`
                    }
                    widgetColor={
                        currentWebsite.widget_color_code !== null
                            ? currentWebsite.widget_color_code
                            : `#420083`
                    }
                    revertBackToOriginalLanguage={revertBackToOriginalLanguage}
                />
            ) : (
                <></>
            )}
        </>
    );
};
export default EditPersonalInformation;
